import { useState, useRef, useEffect, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';

export type LanguageOption = 'en' | 'ua' | 'es' | 'ru';

export const LANGUAGE_OPTIONS: LanguageOption[] = ['en', 'ua', 'es', 'ru'];

export const isSupportedLanguage = (value: string): value is LanguageOption =>
  (LANGUAGE_OPTIONS as string[]).includes(value);

interface LanguageContextType {
  selectedLanguage: LanguageOption;
  setSelectedLanguage: (language: LanguageOption) => void;
}

const DEFAULT_LANGUAGE: LanguageOption = 'en';

const LanguageContext = createContext<LanguageContextType>({
  selectedLanguage: DEFAULT_LANGUAGE,
  setSelectedLanguage: () => {
    throw new Error('LanguageProvider is missing in the component tree');
  }
});

export const useLanguage = (): LanguageContextType => useContext(LanguageContext);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageOption>(DEFAULT_LANGUAGE);

  return (
    <LanguageContext.Provider value={{ selectedLanguage, setSelectedLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

const LanguageDropdown: React.FC = () => {
  const { selectedLanguage, setSelectedLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX, width: rect.width });
    }
  }, [open]);

  const handleSelect = (language: LanguageOption) => {
    setSelectedLanguage(language);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      setOpen((prev) => !prev);
    } else if (e.key === 'ArrowDown') {
      setOpen(true);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const dropdownList = (
    <ul
      className="bg-white border border-[#EDF0F5] rounded-lg shadow-lg z-[9999] absolute"
      style={{
        top: dropdownPos.top,
        left: dropdownPos.left,
        width: dropdownPos.width
      }}
      role="listbox"
      aria-label="Language options"
    >
      {LANGUAGE_OPTIONS.map((language) => (
        <li
          key={language}
          role="option"
          aria-selected={selectedLanguage === language}
          tabIndex={0}
          className={`px-4 py-2 cursor-pointer hover:bg-[#F6F8FF] focus:bg-[#F6F8FF] ${selectedLanguage === language ? 'bg-[#F6F8FF] font-bold' : ''}`}
          onClick={() => handleSelect(language)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleSelect(language);
            }
          }}
        >
          {language}
        </li>
      ))}
    </ul>
  );

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        className="flex items-center gap-2 px-4 py-2 bg-white border border-[#EDF0F5] rounded-lg shadow-sm text-[#165bfb] font-semibold focus:outline-none focus:ring-2 focus:ring-[#165bfb] transition min-w-[90px]"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Select language"
        tabIndex={0}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={handleKeyDown}
      >
        <span>{selectedLanguage}</span>
        <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 20 20">
          <path d="M6 8l4 4 4-4" stroke="#165bfb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && typeof window !== 'undefined' && createPortal(dropdownList, document.body)}
    </div>
  );
};

export default LanguageDropdown;


